/**
 * PriceMapPage · Public UK Fuel Heatmap (iter174 · P4)
 * ───────────────────────────────────────────────────
 * Showcases the FuelPriceMap heatmap component as a standalone public
 * page. The heatmap itself already lives in `components/FuelPriceMap.js`;
 * this page wraps it in a marketing-friendly layout with context,
 * legend, and CTAs so visitors can instantly see where fuel is
 * cheapest across the UK before signing up.
 *
 * Route: /price-map
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import FuelPriceMap from '../components/FuelPriceMap';

const PriceMapPage = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationTried, setLocationTried] = useState(false);

  useEffect(() => {
    document.title = 'UK Fuel Price Map · PricePAL Compare';
    // Best-effort geolocation. If the visitor blocks it, we default to UK-wide.
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
          setLocationTried(true);
        },
        () => setLocationTried(true),
        { timeout: 4000, maximumAge: 60_000 },
      );
    } else {
      setLocationTried(true);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-emerald-50/40" data-testid="price-map-page">
      {/* ── Hero ────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 pt-12 pb-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">
              Live UK Fuel Data · Updated hourly
            </span>
            <h1
              className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-zinc-900 tracking-tight"
              data-testid="price-map-title"
            >
              Where is fuel cheapest today?
            </h1>
            <p className="mt-3 max-w-2xl text-base text-zinc-600" data-testid="price-map-subtitle">
              A live heatmap of every UK forecourt we can see —
              <span className="text-emerald-700 font-semibold"> green</span> is cheap,
              <span className="text-red-600 font-semibold"> red</span> is dear.
              Filter by unleaded, diesel, super or super-diesel.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/register"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 text-sm font-semibold transition"
              data-testid="price-map-cta-register"
            >
              Get personalised alerts →
            </Link>
          </div>
        </div>
      </section>

      {/* ── Map card ─────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 pb-10">
        <div className="rounded-3xl shadow-lg overflow-hidden border border-zinc-200 bg-white">
          {locationTried ? (
            <FuelPriceMap userLocation={userLocation} />
          ) : (
            <div className="p-10 text-center text-zinc-500" data-testid="price-map-locating">
              Looking up your location…
            </div>
          )}
        </div>

        {/* Legend & context under the map */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="legend-green">
            <div className="flex items-center gap-2 font-semibold text-emerald-900">
              <span className="w-3 h-3 rounded-full bg-emerald-500" /> Cheapest 25%
            </div>
            <p className="mt-1 text-xs text-emerald-800/80">
              Head here if you can — significant savings vs regional average.
            </p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="legend-yellow">
            <div className="flex items-center gap-2 font-semibold text-amber-900">
              <span className="w-3 h-3 rounded-full bg-amber-400" /> Middle 50%
            </div>
            <p className="mt-1 text-xs text-amber-800/80">
              Fair prices — worth filling up here if convenient.
            </p>
          </div>
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="legend-red">
            <div className="flex items-center gap-2 font-semibold text-rose-900">
              <span className="w-3 h-3 rounded-full bg-rose-500" /> Priciest 25%
            </div>
            <p className="mt-1 text-xs text-rose-800/80">
              Only fill up if you must — cheaper forecourts nearby.
            </p>
          </div>
        </div>
      </section>

      {/* ── Info + CTAs ──────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-zinc-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-zinc-900" data-testid="how-we-source-title">
              How we source this
            </h2>
            <p className="mt-2 text-sm text-zinc-600">
              We aggregate live prices from CMA Open Data (the UK's official
              retailer feeds) — Tesco, Sainsbury&apos;s, Asda, Morrisons, BP,
              Shell, Esso, Texaco, JET and Motor Fuel Group. Updated every hour.
              No paywalls. No stale data.
            </p>
            <Link
              to="/how-it-works"
              className="mt-3 inline-block text-sm font-medium text-emerald-700 hover:text-emerald-800"
              data-testid="link-how-it-works"
            >
              How it works →
            </Link>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-600 text-white p-6">
            <h2 className="text-lg font-semibold" data-testid="cta-alerts-title">Never overpay again</h2>
            <p className="mt-2 text-sm text-emerald-50">
              PricePAL alerts you the moment a forecourt near your postcode
              drops below your fuel budget — free forever for NHS & Blue Light.
            </p>
            <div className="mt-4 flex gap-2">
              <Link
                to="/community"
                className="rounded-full bg-white text-emerald-700 px-4 py-2 text-sm font-semibold hover:bg-emerald-50"
                data-testid="cta-community"
              >
                Free for NHS/Blue Light
              </Link>
              <Link
                to="/pricing"
                className="rounded-full border border-white/60 text-white px-4 py-2 text-sm font-semibold hover:bg-white/10"
                data-testid="cta-pricing"
              >
                See pricing
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PriceMapPage;
